(function () {
  'use strict';

  const { debounce, GalleryCard, QuickAdd } = window.Universe;

  class Search {
    constructor(wrap) {
      if (wrap) {
        this.init(wrap);
      }
    }

    setup() {
      this.onInputDebounced = debounce(() => {
        this.onChange();
      }, 300);
      this.DOM.input.addEventListener('input', this.onInputDebounced);
      this._onInputKeyDown = (e) => {
        this.onInputKeyDown(e);
      };
      this.DOM.input.addEventListener('keydown', this._onInputKeyDown);
      if (this.DOM.inputReset) {
        this._onReset = () => {
          this.onReset();
        };
        this.DOM.inputReset.addEventListener('click', this._onReset);
      }

      this.DOM.form = this.DOM.input.closest('form');
      if (this.DOM.form) {
        this._onFormSubmit = (event) => {
          this.onFormSubmit(event);
        };
        this.DOM.form.addEventListener('submit', this._onFormSubmit);
      }
    }

    getSearchValue() {
      return this.DOM.input.value.trim();
    }

    onChange() {
      const newSearchTerm = this.getSearchValue();
      this.searchTerm = newSearchTerm;

      if (!newSearchTerm) {
        this.abortController.abort();
        this.abortController = new AbortController();
        this.DOM.wrap.classList.remove('is-success');
        if (this.DOM.appendContainer) {
          this.DOM.appendContainer.innerHTML = '';
          this.DOM.appendContainer.removeAttribute('aria-busy');
        }
        this.DOM.input.setAttribute('aria-expanded', 'false');
        this.clearActiveOptions();
        this.destroyCards();
        if (this.DOM.liveRegion) this.DOM.liveRegion.textContent = '';
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onReset() {
      this.DOM.input.value = '';
      this.DOM.input.focus();

      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();

      this.onChange();
    }

    onFormSubmit(event) {
      event.preventDefault();
      const searchTerm = this.getSearchValue();
      if (searchTerm) {
        this.redirectToMainSearch();
      }
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(/ /g, '-').toLowerCase();

      if (this.cachedResults[queryKey]) {
        console.log('Using cached results', this.cachedResults[queryKey]);
        return;
      }

      if (this.DOM.appendContainer) {
        this.DOM.appendContainer.setAttribute('aria-busy', 'true');
      }

      fetch(
        window.routes.predictive_search_url +
          '?q=' +
          encodeURIComponent(searchTerm) +
          '&section_id=u-predictive-search',
        {
          signal: this.abortController.signal,
        },
      )
        .then((response) => {
          if (!response.ok) {
            this.DOM.wrap.classList.remove('is-success');
            this.DOM.appendContainer.innerHTML = '';
            const error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#gsearch-results').innerHTML;

          this.DOM.wrap.classList.add('is-success');
          this.DOM.appendContainer.innerHTML = resultsMarkup;
          this.DOM.appendContainer.setAttribute('aria-busy', 'false');
          this.DOM.input.setAttribute('aria-expanded', 'true');

          this.setupCards();
          this.setupResultsEventListeners();
          this.setupOptionNavigation();
          this.announceResults();
        })
        .catch((error) => {
          if (error && error.code === 20) {
            return;
          }
          throw error;
        });
    }

    setupResultsEventListeners() {
      const resultItems = this.DOM.appendContainer.querySelectorAll(
        '.gsearch__result-item a, .gsearch__result-item button',
      );
      resultItems.forEach((item) => {
        item.addEventListener('click', (event) => {
          this.onResultClick(event);
        });
      });

      const viewAllButtons = this.DOM.appendContainer.querySelectorAll('.gsearch__head a[href*="search"]');
      if (viewAllButtons.length > 0) {
        viewAllButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            this.onViewAllClick(event);
          });
        });
      }
    }

    onInputKeyDown(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const options = this.getResultOptions();
        if (options.length > 0) {
          this.setActiveOption(options[0]);
        } else if (this.DOM.viewAllBtn) {
          this.DOM.viewAllBtn.focus();
        }
      }
    }

    onOptionKeyDown(e) {
      const options = this.getResultOptions();
      const currentIndex = options.indexOf(e.currentTarget);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex < options.length - 1) {
            this.setActiveOption(options[currentIndex + 1]);
          } else if (this.DOM.viewAllBtn) {
            this.clearActiveOptions();
            this.DOM.viewAllBtn.focus();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex === 0) {
            this.clearActiveOptions();
            this.DOM.input.focus();
          } else {
            this.setActiveOption(options[currentIndex - 1]);
          }
          break;
        case 'Enter': {
          e.preventDefault();
          const link = e.currentTarget.querySelector('a[href]');
          if (link) link.click();
          break;
        }
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          this.closePredictiveSearch();
          break;
      }
    }

    onViewAllKeyDown(e) {
      const options = this.getResultOptions();
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (options.length > 0) {
            this.setActiveOption(options[options.length - 1]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          this.closePredictiveSearch();
          break;
      }
    }

    getResultOptions() {
      if (!this.DOM.appendContainer) return [];
      return Array.from(this.DOM.appendContainer.querySelectorAll('[role="option"]'));
    }

    setActiveOption(option) {
      this.getResultOptions().forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');
      option.focus();
    }

    clearActiveOptions() {
      this.getResultOptions().forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
    }

    setupOptionNavigation() {
      this._boundOnOptionKeyDown = (e) => {
        this.onOptionKeyDown(e);
      };

      this.getResultOptions().forEach((opt) => {
        opt.setAttribute('tabindex', '-1');
        opt.addEventListener('keydown', this._boundOnOptionKeyDown);
        opt.querySelectorAll('a[href], button').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
      });

      this.DOM.viewAllBtn = this.DOM.appendContainer.querySelector('.gsearch__head a[href], .gsearch__head button');
      if (this.DOM.viewAllBtn) {
        this._boundOnViewAllKeyDown = (e) => {
          this.onViewAllKeyDown(e);
        };
        this.DOM.viewAllBtn.addEventListener('keydown', this._boundOnViewAllKeyDown);
      }
    }

    announceResults() {
      if (!this.DOM.liveRegion) return;
      const countLabel = this.DOM.appendContainer.querySelector('.gsearch__head-label');
      const notFound = this.DOM.appendContainer.querySelector('.gsearch__not-found-title');
      if (countLabel) {
        this.DOM.liveRegion.textContent = countLabel.textContent.trim();
      } else if (notFound) {
        this.DOM.liveRegion.textContent = notFound.textContent.trim();
      }
    }

    setupCards() {
      this.destroyCards();

      this.DOM.cards = this.DOM.appendContainer.querySelectorAll('.js-gallery-card');
      this.DOM.quickAdd = document.querySelector('.js-quick-add-container');

      if (this.DOM.cards) {
        this.cards = [];
        this.quickAdds = [];

        this.DOM.cards.forEach((card) => {
          const cardInstance = new GalleryCard(card);
          this.cards.push(cardInstance);

          if (this.DOM.quickAdd) {
            const cardQuickAdd = new QuickAdd(card, this.DOM.quickAdd);
            this.quickAdds.push(cardQuickAdd);
          }
        });
      }
    }

    destroyCards() {
      if (this.cards)
        this.cards.forEach((card) => {
          card.destroy();
        });
      if (this.quickAdds)
        this.quickAdds.forEach((quickAdd) => {
          quickAdd.destroy();
        });

      if (this._boundOnOptionKeyDown) {
        this.getResultOptions().forEach((opt) => {
          opt.removeEventListener('keydown', this._boundOnOptionKeyDown);
        });
      }
      if (this.DOM.viewAllBtn && this._boundOnViewAllKeyDown) {
        this.DOM.viewAllBtn.removeEventListener('keydown', this._boundOnViewAllKeyDown);
      }

      this.cards = undefined;
      this.quickAdds = undefined;
      this.DOM.cards = undefined;
      this.DOM.quickAdds = undefined;
      this.DOM.viewAllBtn = undefined;
    }

    onResultClick(event) {
      if (event.target.href && !event.target.href.includes('javascript:')) {
        return;
      }
      this.redirectToMainSearch();
    }

    onViewAllClick(event) {
      event.preventDefault();
      this.redirectToMainSearch();
    }

    redirectToMainSearch() {
      const searchTerm = this.getSearchValue();
      if (searchTerm) {
        this.closePredictiveSearch();
        const searchUrl = window.routes.search_url + '?q=' + encodeURIComponent(searchTerm);
        window.location.href = searchUrl;
      }
    }

    closePredictiveSearch() {
      const searchLayer = document.querySelector('.js-gsearch');
      if (searchLayer) {
        const closeButton = document.querySelector('.js-gsearch-close');
        if (closeButton) {
          closeButton.click();
        }
      }
    }

    destroy() {
      if (this.DOM) {
        if (this.DOM.input) this.DOM.input.removeEventListener('input', this.onInputDebounced);
        if (this.DOM.input) this.DOM.input.removeEventListener('keydown', this._onInputKeyDown);
        if (this.DOM.inputReset && this._onReset) this.DOM.inputReset.removeEventListener('click', this._onReset);
        if (this.DOM.form && this._onFormSubmit) this.DOM.form.removeEventListener('submit', this._onFormSubmit);
        if (this.DOM.liveRegion) this.DOM.liveRegion.remove();
        this.destroyCards();
        this.DOM = undefined;
      }
    }

    init(wrap) {
      this.DOM = {};
      this.DOM.wrap = wrap;
      this.DOM.input = this.DOM.wrap.querySelector('.js-gsearch-input');
      this.DOM.inputReset = this.DOM.wrap.querySelector('.js-gsearch-input-reset');
      this.DOM.appendContainer = this.DOM.wrap.querySelector('.js-gsearch-append');

      this.cachedResults = {};
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.cards = [];
      this.quickAdds = [];

      this.DOM.liveRegion = document.createElement('div');
      this.DOM.liveRegion.setAttribute('aria-live', 'polite');
      this.DOM.liveRegion.setAttribute('aria-atomic', 'true');
      this.DOM.liveRegion.classList.add('visually-hidden');
      this.DOM.wrap.appendChild(this.DOM.liveRegion);

      this.setup();
    }
  }

  window.Universe = window.Universe || {};
  window.Universe.Search = Search;
})();
